import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SaveIcon from '@mui/icons-material/Save';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LanguageIcon from '@mui/icons-material/Language';
import TimerIcon from '@mui/icons-material/Timer';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import { getPublicConfig, updateSystemConfig } from '../services/configService';

// 配置项定义
const configDefinitions = [
  {
    key: 'searchLimit',
    label: '搜索限制次数',
    description: '外部用户每分钟搜索样板的最大次数',
    defaultValue: '5',
    type: 'number',
    section: 'searchSettings',
    icon: <TimerIcon />
  },
  {
    key: 'contactPhone',
    label: '联系电话',
    description: '对外展示的联系电话',
    defaultValue: '',
    type: 'text',
    section: 'contactInfo',
    icon: <ContactPhoneIcon />
  },
  {
    key: 'contactEmail',
    label: '联系邮箱',
    description: '对外展示的联系邮箱',
    defaultValue: '',
    type: 'email',
    section: 'contactInfo',
    icon: <ContactPhoneIcon />
  },
  {
    key: 'contactAddress',
    label: '公司网址',
    description: '对外展示的公司网址',
    defaultValue: '',
    type: 'text',
    section: 'contactInfo',
    icon: <LanguageIcon />
  },
  {
    key: 'companyName',
    label: '公司名称',
    description: '对外展示的公司名称',
    defaultValue: '',
    type: 'text',
    section: 'contactInfo',
    icon: <LanguageIcon />
  }
];

// 分组配置项
const configSections = [
  {
    id: 'searchSettings',
    title: '搜索设置',
    description: '控制外部用户搜索样板的行为和限制'
  },
  {
    id: 'contactInfo',
    title: '联系信息',
    description: '对外展示的联系方式和公司信息'
  }
];

const ConfigCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)'
  }
}));

const SystemConfig = () => {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // 加载系统配置
  useEffect(() => {
    const loadConfigs = async () => {
      try {
        setLoading(true);
        const configKeys = configDefinitions.map(def => def.key);
        const configData = await getPublicConfig(configKeys);
        
        // 合并默认值和实际值
        const mergedConfigs = {};
        configDefinitions.forEach(def => {
          mergedConfigs[def.key] = configData[def.key] || def.defaultValue;
        });
        
        setConfigs(mergedConfigs);
      } catch (error) {
        console.error('加载系统配置失败:', error);
        showSnackbar('加载系统配置失败', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    loadConfigs();
  }, []);
  
  // 配置项变更处理
  const handleConfigChange = (key, value) => {
    setConfigs(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  // 保存配置
  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      
      // 直接使用configs对象，不需要转换为数组
      // 这样后端就会收到正确的键值对
      await updateSystemConfig(configs);
      
      showSnackbar('系统配置保存成功', 'success');
    } catch (error) {
      console.error('保存系统配置失败:', error);
      showSnackbar('保存系统配置失败', 'error');
    } finally {
      setSaving(false);
    }
  };
  
  // 显示消息提示
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  // 关闭消息提示
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // 渲染配置输入控件
  const renderConfigInput = (config) => {
    const { key, type, label } = config;
    
    switch (type) {
      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            label={label}
            value={configs[key] || ''}
            onChange={(e) => handleConfigChange(key, e.target.value)}
            margin="normal"
            InputProps={{
              inputProps: { min: 1 }
            }}
          />
        );
        
      case 'select':
        return (
          <FormControl fullWidth margin="normal">
            <InputLabel>{label}</InputLabel>
            <Select
              value={configs[key] || ''}
              onChange={(e) => handleConfigChange(key, e.target.value)}
              label={label}
            >
              {config.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
        
      default:
        return (
          <TextField
            fullWidth
            type={type}
            label={label}
            value={configs[key] || ''}
            onChange={(e) => handleConfigChange(key, e.target.value)}
            margin="normal"
          />
        );
    }
  };
  
  // 按部分渲染配置项
  const renderConfigSection = (sectionId) => {
    const sectionConfigs = configDefinitions.filter(
      config => config.section === sectionId
    );
    
    return (
      <>
        {sectionConfigs.map((config) => (
          <Box key={config.key} mb={2}>
            <Box display="flex" alignItems="center" mb={1}>
              <Box mr={1} display="flex" alignItems="center" color="primary.main">
                {config.icon}
              </Box>
              <Typography variant="subtitle1">{config.label}</Typography>
              <Tooltip title={config.description}>
                <IconButton size="small">
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {renderConfigInput(config)}
            <Divider sx={{ mt: 2 }} />
          </Box>
        ))}
      </>
    );
  };
  
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <LinearProgress />
        <Box mt={2}>
          <Typography align="center">加载系统配置...</Typography>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        系统配置
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" paragraph>
        管理系统的全局配置项，包括样板搜索限制、联系信息等
      </Typography>
      
      <Box mt={4}>
        <Grid container spacing={3}>
          {configSections.map((section) => (
            <Grid item xs={12} md={6} key={section.id}>
              <ConfigCard>
                <CardHeader
                  title={section.title}
                  subheader={section.description}
                />
                <CardContent>
                  {renderConfigSection(section.id)}
                </CardContent>
              </ConfigCard>
            </Grid>
          ))}
        </Grid>
      </Box>
      
      <Box mt={4} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSaveConfig}
          disabled={saving}
        >
          {saving ? '保存中...' : '保存配置'}
        </Button>
      </Box>
      
      {/* 消息提示 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SystemConfig;
